import React, { useState, useEffect, useRef } from 'react';

const TourGuide = ({ steps, isOpen, onRequestClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [targetRect, setTargetRect] = useState(null);
    const tooltipRef = useRef(null);

    // Reset step on open
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
        }
    }, [isOpen]);

    // Scroll to target when step changes (run ONCE per step change)
    useEffect(() => {
        if (isOpen && steps[currentStep]) {
            const target = document.querySelector(steps[currentStep].target);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
        }
    }, [isOpen, currentStep, steps]);

    // Update position tracking (runs on scroll/resize, DOES NOT SCROLL)
    useEffect(() => {
        if (!isOpen || !steps[currentStep]) return;

        const updatePosition = () => {
            const target = document.querySelector(steps[currentStep].target);
            if (target) {
                const rect = target.getBoundingClientRect();
                setTargetRect({
                    top: rect.top + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                    height: rect.height,
                    bottom: rect.bottom + window.scrollY,
                    right: rect.right + window.scrollX,
                    // Track viewport relative positions for smart placement
                    viewportTop: rect.top,
                    viewportBottom: rect.bottom,
                    viewportHeight: window.innerHeight
                });
            } else {
                setTargetRect(null);
            }
        };

        // Initial update
        updatePosition();

        // Update loop to handle animation/layout shifts
        const interval = setInterval(updatePosition, 100);

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition);

        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };
    }, [isOpen, currentStep, steps]);

    if (!isOpen || !steps[currentStep]) return null;

    const step = steps[currentStep];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            onRequestClose();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleSkip = () => {
        onRequestClose();
    };

    // Calculate tooltip position
    let tooltipStyle = {};
    let arrowClass = 'arrow-top'; // Default: tooltip is below target, arrow points up

    if (targetRect) {
        // Default: Bottom
        tooltipStyle = {
            top: targetRect.bottom + 15,
            left: targetRect.left + (targetRect.width / 2),
            transform: 'translateX(-50%)' // Center horizontally
        };
        arrowClass = 'arrow-top';

        // Check availability of space below
        const spaceBelow = targetRect.viewportHeight - targetRect.viewportBottom;
        const spaceAbove = targetRect.viewportTop;
        const estimatedHeight = 300; // Estimate height for tooltip with content

        // If less than estimated height below and MORE space above (and enough space above), flip to Top
        if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow && spaceAbove > 200) {
            tooltipStyle.top = Math.max(10, targetRect.top - 15); // Ensure not off-screen top
            tooltipStyle.transform = 'translate(-50%, -100%)'; // Move up by its own height
            arrowClass = 'arrow-bottom';
        }

        // Horizontal Clamping - Ensure it doesn't go off-screen left/right
        // Center text is `targetRect.left + width/2`. 
        // Tooltip width is roughly 300px (150px each side).
        const centerX = targetRect.left + (targetRect.width / 2);

        if (centerX < 160) {
            // Too close to left edge
            tooltipStyle.left = targetRect.left; // Align left
            if (arrowClass === 'arrow-bottom') {
                tooltipStyle.transform = 'translate(0, -100%)';
            } else {
                tooltipStyle.transform = 'none';
            }
            // We would need to move the arrow if we do this, 
            // but for now simpler keeps user able to see text.
        } else if (centerX > window.innerWidth - 160) {
            // Too close to right edge
            // Logic to pin right...
        }
    }

    return (
        <div className="tour-overlay">
            {/* Highlight Box */}
            {targetRect && (
                <div
                    className="tour-highlight"
                    style={{
                        top: targetRect.top,
                        left: targetRect.left,
                        width: targetRect.width,
                        height: targetRect.height,
                    }}
                />
            )}

            {/* Tooltip */}
            <div
                className={`tour-tooltip ${arrowClass}`}
                style={tooltipStyle}
                ref={tooltipRef}
            >
                <div className="tour-header">
                    <h4>{step.title}</h4>
                    <button className="close-btn" onClick={handleSkip}>×</button>
                </div>
                <div className="tour-content">
                    <p>{step.content}</p>
                </div>
                <div className="tour-footer">
                    <div className="step-indicator">
                        {currentStep + 1} / {steps.length}
                    </div>
                    <div className="tour-controls">
                        {currentStep > 0 && (
                            <button className="tour-btn secondary" onClick={handlePrev}>Back</button>
                        )}
                        <button className="tour-btn primary" onClick={handleNext}>
                            {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TourGuide;
